import { AlertColor, AlertPropsColorOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { AxiosError } from "axios";
import { MeasurementUnitCode } from "../../common/models/CommonModels";
import useTranslation from "../../shared/hooks/useTranslation";

export interface NotificationProps {
  openSnackbar: boolean;
  severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
  alertMessage: string;
}

class ApiError {
  static AXIOS_UNAUTHORIZED_ERROR_MESSAGE = "Request failed with status code 401";

  static DEFAULT_ERROR_MESSAGE = 'Oops, something went wrong';
  static ACCESS_DENIED_ERROR_MESSAGE = 'Access denied';
  static TOKEN_EXPIRED_ERROR_MESSAGE = 'Your session has run out, please log in again';

  message!: string;
  code?: number;

  constructor(message: string, code?: number) {
    this.message = message;
    this.code = code;
  }

  static create(error: any): ApiError {
    if (!error) {
      return new ApiError(this.DEFAULT_ERROR_MESSAGE);
    }

    if (typeof error === "string") {
      return new ApiError(error);
    }

    const axiosError = error as AxiosError<any>;

    if (axiosError.response) {
      if (axiosError.response.data) {
        if (typeof axiosError.response.data === "string") {
          return new ApiError(axiosError.response.data);
        }

        return {
          message: axiosError.response.data.message || this.DEFAULT_ERROR_MESSAGE,
          code: axiosError.response.data.code
        };
      }

      if (axiosError.response.status === 403) {
        return new ApiError(this.ACCESS_DENIED_ERROR_MESSAGE);
      }

      if (axiosError.response.status === 401) {
        return new ApiError(this.TOKEN_EXPIRED_ERROR_MESSAGE, 401);
      }
    }

    if (axiosError.message) {
      if (axiosError.message === this.AXIOS_UNAUTHORIZED_ERROR_MESSAGE) {
        return new ApiError(this.TOKEN_EXPIRED_ERROR_MESSAGE, 401);
      }

      return new ApiError(axiosError.message);
    }

    return new ApiError(this.DEFAULT_ERROR_MESSAGE);
  }
}

export class NotificationMessage {
  message: string = '';
  errorCode?: number;

  static error(error: any): NotificationMessage {
    const apiError = ApiError.create(error);
    return { message: apiError.message, errorCode: apiError.code };
  }
}

export interface QueryBase<TSortParameter> {
  search?: string;
  sortBy?: TSortParameter;
  sortDesc?: boolean;
  pageNo: number;
  pageSize: number;
}

export interface Page<T> {
  pageNo: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  items: T[];
}

export function getName(firstName: string, lastName: string): string {
  return [firstName, lastName].join(' ').trim();
}

export const enumToArray = <TEnum extends number>(obj: any): TEnum[] => {
  const values = Object.keys(obj).map(x => Number(x)).filter(x => !isNaN(x)).map(x => x as TEnum);
  return values;
}

export const classNames = (classNameA?: string, classNameB?: string) => {
  return [classNameA, classNameB].filter(x => x).join(" ");
};

export const dimensions = (height?: number, width?: number, depth?: number): string => {
  const dimensionsString = [[height, "H"], [width, "W"], [depth, "D"]]
    .filter(x => x[0])
    .map(x => `${x[0]}(${x[1]})`)
    .join(" x ");

  return dimensionsString;
};

export const quantity = (value?: number): string => {
  const quantityString = value === undefined ? "" :
    value === 1 ? `${value} pc` :
      `${value} pcs`;

  return quantityString;
};

export enum WarrantyTypeCode {
  threeMonths = 1,
  sixMonths = 2,
  oneYear = 3,
  twoYears = 4
}

class WarrantyTypes {
  private readonly nameMap = {} as any;
  private readonly periodMap = {} as any;

  constructor() {
    this.nameMap[WarrantyTypeCode.threeMonths] = "3 months";
    this.nameMap[WarrantyTypeCode.sixMonths] = "6 mounths";
    this.nameMap[WarrantyTypeCode.oneYear] = "1 year";
    this.nameMap[WarrantyTypeCode.twoYears] = "2 years";

    this.periodMap[WarrantyTypeCode.threeMonths] = 3;
    this.periodMap[WarrantyTypeCode.sixMonths] = 6;
    this.periodMap[WarrantyTypeCode.oneYear] = 12;
    this.periodMap[WarrantyTypeCode.twoYears] = 24;
  }

  warranty = (code: WarrantyTypeCode) => {
    return this.nameMap[code] || '';
  };

  toWarrantyPeriodMonths = (code: WarrantyTypeCode) => {
    return this.periodMap[code] || '';
  };

  formatWarrantyFullName = (periodMonths?: number, noTextWarranty?: boolean): string | undefined => {
    if (!periodMonths) {
      return undefined;
    }

    const periodYears = Math.floor(periodMonths / 12);

    if (noTextWarranty) {
      return periodYears >= 1 ? `${periodYears}-year` : `${periodMonths}-month`
    }

    return periodYears >= 1 ? `${periodYears}-year warranty` : `${periodMonths}-month warranty`;
  };
}

export const { warranty, toWarrantyPeriodMonths, formatWarrantyFullName } = new WarrantyTypes();

export enum CustomerSizeCode {
  from1To10 = 1,
  from11To50 = 2,
  from51To200 = 3,
  from201To500 = 4,
  from501To1000 = 5,
  from1001To5000 = 6,
  from5001To10000 = 7,
  from10001 = 8
}

export enum CustomerTurnoverCode {
  lessThan10000 = 1,
  from10000To100000 = 2,
  from100000To1000000 = 3,
  from1000000 = 4
}

class CustomerTurnovers {
  private readonly map = {} as any;

  constructor() {
    this.map[CustomerTurnoverCode.lessThan10000] = "Below 10,000€";
    this.map[CustomerTurnoverCode.from10000To100000] = "10,000-100,000€";
    this.map[CustomerTurnoverCode.from100000To1000000] = "100,000-1,000,000€";
    this.map[CustomerTurnoverCode.from1000000] = "Over 1,000,000€";
  }

  customerTurnover = (code: CustomerTurnoverCode) => {
    return this.map[code] || '';
  };
}

class CustomerSizes {
  private readonly map = {} as any;

  constructor() {
    this.map[CustomerSizeCode.from1To10] = "1-10";
    this.map[CustomerSizeCode.from11To50] = "11-50";
    this.map[CustomerSizeCode.from51To200] = "51-200";
    this.map[CustomerSizeCode.from201To500] = "201-500";
    this.map[CustomerSizeCode.from501To1000] = "501-1000";
    this.map[CustomerSizeCode.from1001To5000] = "1001-5000";
    this.map[CustomerSizeCode.from5001To10000] = "5001-10000";
    this.map[CustomerSizeCode.from10001] = "More 10001";
  }

  customerSize = (code: CustomerSizeCode) => {
    return this.map[code] || '';
  };
}

export const { customerTurnover } = new CustomerTurnovers();
export const { customerSize } = new CustomerSizes();

export function useCustomerSizeLabel() {
  const { translate } = useTranslation("settings");
  return (code: CustomerSizeCode) => {
    const key = CustomerSizeCode[code];
    return translate(`customerSize.${key}`);
  };
}

export function useCustomerTurnoverLabel() {
  const { translate } = useTranslation("settings");
  return (code: CustomerTurnoverCode) => {
    const key = CustomerTurnoverCode[code];
    return translate(`customerTurnover.${key}`);
  };
}

export enum DayOfWeek {
  monday = 0,
  tuesday = 1,
  wednesday = 2,
  thursday = 3,
  friday = 4,
  saturday = 5,
  sunday = 6,
}

class DayOfWeekCode {
  private readonly map = {} as any;

  constructor() {
    this.map[DayOfWeek.monday] = "Monday";
    this.map[DayOfWeek.tuesday] = "Tuesday";
    this.map[DayOfWeek.wednesday] = "Wednesday";
    this.map[DayOfWeek.thursday] = "Thursday";
    this.map[DayOfWeek.friday] = "Friday";
    this.map[DayOfWeek.saturday] = "Saturday";
    this.map[DayOfWeek.sunday] = "Sunday";
  }

  dayOfWeekCode = (code: DayOfWeek) => {
    return this.map[code] || '';
  };
}

export const { dayOfWeekCode } = new DayOfWeekCode();

export const compressWeekDays = (days: DayOfWeek[]) => {
  if (!days.length) {
    return "";
  }

  const daysSort = days ? [...days] : [];
  daysSort.sort((a, b) => a - b);

  const result = [];
  let start = daysSort[0];
  let end = daysSort[0];

  for (let i = 1; i < daysSort.length; i++) {
    if (daysSort[i] === end + 1) {
      end = daysSort[i];
    } else {
      if (start === end) {
        result.push(dayOfWeekCode(start).substr(0, 3));
      } else {
        result.push(`${dayOfWeekCode(start).substr(0, 3)}-${dayOfWeekCode(end).substr(0, 3)}`);
      }
      start = daysSort[i];
      end = daysSort[i];
    }
  }

  if (start === end) {
    result.push(dayOfWeekCode(start).substr(0, 3));
  } else {
    result.push(`${dayOfWeekCode(start).substr(0, 3)}-${dayOfWeekCode(end).substr(0, 3)}`);
  }

  return result.join(", ");
}

export enum PaymentMethodCode {
  visa = 1,
  masterCard = 2,
  invoice = 3,
  payPal = 4
}

class PaymentCode {
  private readonly map = {} as any;

  constructor() {
    this.map[PaymentMethodCode.visa] = "Visa";
    this.map[PaymentMethodCode.masterCard] = "MasterCard";
    this.map[PaymentMethodCode.invoice] = "Invoice";
    this.map[PaymentMethodCode.payPal] = "PayPal";
  }

  paymentCode = (code: PaymentMethodCode) => {
    return this.map[code] || '';
  };
}

export const { paymentCode } = new PaymentCode();

export interface FileModel {
  name?: string;
  content?: string;
}

export enum ShippingRegionCode {
  country = 1,
  EU = 2,
  worldwide = 3
}

export enum ShippingOptionCode {
  standart = 1,
  express = 2,
  selfPickUp = 3
}

class ShippingCode {
  private readonly map = {} as any;

  constructor() {
    this.map[ShippingOptionCode.standart] = "Standard";
    this.map[ShippingOptionCode.express] = "Express";
    this.map[ShippingOptionCode.selfPickUp] = "Self pick-up";
  }

  shippingCode = (code: ShippingOptionCode) => {
    return this.map[code] || '';
  };
}

export const { shippingCode } = new ShippingCode();

export const calculateTotalPrice = (price: number | undefined, quantity: number | undefined) => {
  if (price && quantity) {
    return price * quantity;
  }
};

export const sortManufacturersCategoriesByName = (array: Array<{ id: string, name: string }>) => {
  const newArray = array ? [...array] : [];
  const sortArray = newArray.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
  return sortArray;
};

export function stringToColor(string: string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

export function stringAvatar(name: string) {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: name.includes(' ') ? `${name.split(' ')[0][0]}${name.split(' ')[1][0]}` : `${name.split('').shift()}`
  }
}

export class Quantity {
  value?: number;
  unitCode!: MeasurementUnitCode;
}

export const formatFileSize = (size: number, precision = 1) => {
  if (size === 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB"];
  const index = Math.floor(Math.log(size) / Math.log(1024));
  return (size / Math.pow(1024, index)).toFixed(precision) + " " + units[index];
};

export const MAX_FILE_SIZE_MB = 10 + 1;